import React, {useState} from 'react';
import {Button, Table, Modal, Form, Input, message, Popconfirm} from 'antd';
import {SystemDto, SystemControllerApi, DeletionError} from '../api';
import {useKeycloakHeader} from "../config/Config";
import {DeleteOutlined, EditOutlined, PlusOutlined} from "@ant-design/icons";
import DeletionErrorModal from "../components/modal/error/DeletionErrorModal";
import {useSystemContext} from "../utils/hook/SystemProvider";

const SystemOverview: React.FC = () => {
    const [form] = Form.useForm();
    const headers = useKeycloakHeader();
    const { systems, triggerRefresh } = useSystemContext();
    const systemControllerApi = new SystemControllerApi();

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [currentSystem, setCurrentSystem] = useState<SystemDto | null>(null);
    const [loading, setLoading] = useState(false);

    // Für das Fehlermeldungsmodal
    const [deletionErrorList, setDeletionErrorList] = useState<DeletionError[]>([]);
    const [deletionModalVisible, setDeletionModalVisible] = useState<boolean>(false);
    const errorMessage = "Dem System sind noch Tests/Komponenten zugeordnet. Bitte lösche diese oder ordne sie einem anderen System zu.";


    const handleCloseDeletionModal = ()=> {
      setDeletionModalVisible(false);
    }

    // Öffnet das Modal zum Erstellen oder Bearbeiten eines Systems
    const showModal = (system?: SystemDto) => {
        setCurrentSystem(system || null);
        form.setFieldsValue(system || {name: ''});
        setIsModalVisible(true);
    };

    // Schließt das Modal und setzt das Formular zurück
    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
    };

    // Fügt ein neues System hinzu oder aktualisiert ein vorhandenes
    const handleSaveSystem = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);
            if (currentSystem && currentSystem.id) {
                // Update vorhandenes System
                systemControllerApi.updateSystem(currentSystem.id, values, headers).then((response) => {
                    message.success("System erfolgreich aktualisiert!");
                    triggerRefresh();
                }).catch((error) => {
                    message.error("Es ist ein Fehler beim Aktualisieren des Systems aufgetreten!");
                });

            } else {
                // Neues System erstellen
                systemControllerApi.createSystem(values, headers).then((response) => {
                    message.success("System erfolgreich erstellt!");
                    handleCancel();
                    triggerRefresh();
                }).catch((errorMessage) => {
                    message.error({
                        content: 'Fehler beim Anlegen eines Systems: ' + errorMessage.response.data,
                        duration: 15
                    });
                });

            }

        } catch (error) {
            console.log(error);
            message.error("Fehler beim Speichern des Systems!",);
        } finally {
            setLoading(false);
        }
    };

    // Löscht ein System
    const handleDeleteSystem = (id: number) => {
        systemControllerApi.deleteSystem(id, headers).then((r) => {
            message.success("System erfolgreich gelöscht!");
            triggerRefresh();
        }).catch((error) => {
            if(error.response?.data){
                let errorList: DeletionError[] = error.response.data;
                setDeletionErrorList(errorList);
                setDeletionModalVisible(true);
            } else {
                message.error({content: "Es ist ein unerwarteter Fehler aufgetreten! ", duration: 30});
            }
        })
    };

    // Konfiguration der Spalten für die Tabelle
    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Beschreibung',
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: 'Aktionen',
            key: 'actions',
            render: (_: any, record: SystemDto) => (
                <>
                    <Button onClick={() => showModal(record)}>
                        <EditOutlined style={{fontSize: '16px', color: '#1890ff'}}/>
                    </Button>
                    <Popconfirm
                        title="Willst du das System wirklich löschen?"
                        onConfirm={() => record.id ? handleDeleteSystem(record.id)
                            : message.error("System nicht gefunden!")}
                        okText="Ja"
                        cancelText="Nein"
                    >
                        <Button danger>
                            <DeleteOutlined/>
                        </Button>
                    </Popconfirm>
                </>
            ),
        },
    ];

    return (
        <div>
            <h2>Systeme verwalten</h2>
            <Button type="primary" onClick={() => showModal()}>
                <PlusOutlined/>
            </Button>
            <Table
                dataSource={systems}
                columns={columns}
                rowKey="id"
                loading={loading}
                style={{marginTop: 20}}
                locale={{emptyText: 'Keine Daten vorhanden'}}
            />
            <Modal
                title={currentSystem ? 'System bearbeiten' : 'Neues System hinzufügen'}
                open={isModalVisible}
                onOk={handleSaveSystem}
                onCancel={handleCancel}
                confirmLoading={loading}
                okText="Speichern"
                cancelText="Abbrechen"
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="name"
                        label="Systemname"
                        rules={[{required: true, message: 'Bitte einen Systemnamen eingeben!'},
                            {max: 80, message: 'Systemname darf nicht mehr als 80 Zeichen überschreiten!'},
                            {min: 2, message: 'Systemname muss aus mindestens 2 Zeichen bestehen!'}]}
                    >
                        <Input placeholder="Systemname"/>
                    </Form.Item>
                    <Form.Item
                        name="description"
                        label="Systembeschreibung"
                        rules={[{required: true, message: 'Bitte eine Beschreibung eingeben!'}]}
                    >
                        <Input placeholder="Systembeschreibung"/>
                    </Form.Item>
                </Form>
            </Modal>
            <DeletionErrorModal errorList={deletionErrorList}
                                modalVisible={deletionModalVisible}
                                onClose={handleCloseDeletionModal}
                                errorMessage={errorMessage}/>
        </div>
    );
};

export default SystemOverview;
